// ============================================================
// FILE: CreditBalance.js
// LOCATION: C:\gardenmarket\web\src\components\CreditBalance.js
// ============================================================
// PURPOSE:
//   Credit balance display and Stripe-powered top-up widget.
//
// STRIPE FLOW:
//   1. User selects amount → clicks "Pay"
//   2. API creates PaymentIntent → returns client_secret
//   3. Stripe.js card element collects card details
//   4. User confirms → Stripe charges card
//   5. API credits account via confirm-topup
//   6. Balance refreshes
//
// TEST CARD: 4242 4242 4242 4242 | any future expiry | any CVC
// ============================================================

import { useState, useEffect, useRef } from 'react';
import client from '../api/client';
import { colors, radius, card, cardHeader } from '../theme/theme';

const TOP_UP_AMOUNTS = [10, 20, 50, 100];
const FEE_TIERS = [
    { label: 'Up to $25',   fee: '$1' },
    { label: '$26 – $100',  fee: '$2' },
    { label: '$101 – $500', fee: '$5' },
    { label: '$501+',       fee: '$10' },
];
const CURRENCY_CONFIG = {
    AUD: { symbol: 'A$',  label: 'Australian dollars',  flag: 'AU' },
    NZD: { symbol: 'NZ$', label: 'New Zealand dollars', flag: 'NZ' },
    USD: { symbol: 'US$', label: 'US dollars',          flag: 'US' },
};

function fmt(amount, currency) {
    const c = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.AUD;
    return c.symbol + Math.abs(Number(amount)).toFixed(2);
}

let stripeInstance = null;
function loadStripe(key) {
    return new Promise(resolve => {
        if (stripeInstance) { resolve(stripeInstance); return; }
        if (window.Stripe) { stripeInstance = window.Stripe(key); resolve(stripeInstance); return; }
        const s = document.createElement('script');
        s.src = 'https://js.stripe.com/v3/';
        s.onload = () => { stripeInstance = window.Stripe(key); resolve(stripeInstance); };
        document.head.appendChild(s);
    });
}

export default function CreditBalance({ compact = false }) {
    const [data,     setData]     = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [selected, setSelected] = useState(null);
    const [step,     setStep]     = useState('select');
    const [error,    setError]    = useState('');
    const [paying,   setPaying]   = useState(false);
    const mountRef   = useRef(null);
    const cardElRef  = useRef(null);
    const stripeRef  = useRef(null);

    const load = async () => {
        const r = await client.get('/credits/balance');
        setData(r.data);
    };

    useEffect(() => {
        load().catch(console.error).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (step !== 'pay' || !selected) return;
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!key) { setError('Stripe not configured — add REACT_APP_STRIPE_PUBLISHABLE_KEY to web/.env'); return; }
        loadStripe(key).then(stripe => {
            stripeRef.current = stripe;
            const el = stripe.elements().create('card', {
                hidePostalCode: true,
                style: { base: { fontSize:'16px', color:'#111827', '::placeholder': { color:'#6B7280' } } }
            });
            el.mount(mountRef.current);
            cardElRef.current = el;
        });
        return () => { cardElRef.current?.destroy(); cardElRef.current = null; };
    }, [step, selected]);

    const handlePay = async () => {
        if (!selected || paying) return;
        setPaying(true); setError('');
        try {
            const { data: intentData } = await client.post('/credits/create-payment-intent', { amount: selected });
            const { paymentIntent, error: stripeErr } = await stripeRef.current.confirmCardPayment(
                intentData.client_secret,
                { payment_method: { card: cardElRef.current } }
            );
            if (stripeErr) { setError(stripeErr.message); setPaying(false); return; }
            await client.post('/credits/confirm-topup', { payment_intent_id: paymentIntent.id, amount: selected });
            await load();
            setStep('success'); setSelected(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Payment failed.');
        } finally { setPaying(false); }
    };

    if (loading) return <div style={{ padding:'20px', color:colors.textMuted }}>Loading balance...</div>;
    if (!data)   return null;

    const { balance, is_blocked, currency = 'AUD' } = data.balance || {};
    const transactions = data.transactions || [];
    const bal    = parseFloat(balance || 0);
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.AUD;

    if (compact) {
        return (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderRadius:'10px', border:'1px solid', marginBottom:'16px', flexWrap:'wrap', gap:'8px', backgroundColor: is_blocked ? '#FEF2F2' : bal < 5 ? '#FFFBEB' : '#F0FDF4', borderColor: is_blocked ? '#FECACA' : bal < 5 ? '#FDE68A' : '#BBF7D0' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontSize:'0.82rem', color:colors.textFaint }}>Credits ({currency})</span>
                    <span style={{ fontSize:'1.4rem', fontWeight:700, color: is_blocked ? '#991B1B' : bal < 5 ? '#92400E' : '#14532D' }}>{fmt(bal, currency)}</span>
                </div>
                {is_blocked ? <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#991B1B' }}>Top up to see new jobs</span>
                 : bal < 5  ? <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#92400E' }}>Low balance</span>
                 : null}
            </div>
        );
    }

    return (
        <div>
            <div style={{ ...card, borderColor: is_blocked ? '#FECACA' : bal < 5 ? '#FDE68A' : colors.border }}>
                <h3 style={cardHeader}>Credit Balance</h3>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
                    <div>
                        <div style={{ fontSize:'2.5rem', fontWeight:700, color: is_blocked ? '#991B1B' : bal < 5 ? '#92400E' : colors.brand }}>{fmt(bal, currency)}</div>
                        <div style={{ fontSize:'0.85rem', color:colors.textFaint, marginTop:'6px' }}>{config.label} ({currency})</div>
                    </div>
                    <span style={{ padding:'4px 10px', borderRadius:'999px', fontSize:'0.8rem', fontWeight:600, backgroundColor: is_blocked ? '#FEE2E2' : bal < 5 ? '#FEF3C7' : '#DCFCE7', color: is_blocked ? '#991B1B' : bal < 5 ? '#92400E' : '#14532D' }}>
                        {is_blocked ? 'Blocked' : bal < 5 ? 'Low balance' : 'Active'}
                    </span>
                </div>
                {is_blocked && <p style={{ backgroundColor:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'8px', padding:'10px 14px', color:'#991B1B', fontSize:'0.88rem', marginBottom:'16px' }}>Balance below {fmt(-5, currency)}. Top up to continue receiving new jobs.</p>}
                <div style={{ borderTop:`1px solid ${colors.border}`, paddingTop:'14px' }}>
                    <p style={{ fontSize:'0.78rem', color:colors.textFaint, marginBottom:'8px', fontWeight:600 }}>Platform fee per job won ({currency}):</p>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px' }}>
                        {FEE_TIERS.map(t => (
                            <div key={t.label} style={{ textAlign:'center', padding:'8px', backgroundColor:colors.bgPage, borderRadius:'8px' }}>
                                <span style={{ display:'block', fontSize:'0.72rem', color:colors.textFaint, marginBottom:'2px' }}>{t.label}</span>
                                <span style={{ display:'block', fontSize:'1rem', fontWeight:700, color:colors.brand }}>{t.fee}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {step === 'success' && (
                <div style={{ ...card, backgroundColor:'#F0FDF4', borderColor:'#BBF7D0' }}>
                    <p style={{ color:'#14532D', fontWeight:600, fontSize:'1rem', marginBottom:'8px' }}>Payment successful!</p>
                    <p style={{ color:colors.textMuted, fontSize:'0.9rem', marginBottom:'12px' }}>Your credit balance has been updated.</p>
                    <button onClick={() => setStep('select')} style={{ padding:'8px 16px', backgroundColor:colors.brand, color:'white', border:'none', borderRadius:radius.sm, cursor:'pointer', fontWeight:600 }}>Top up again</button>
                </div>
            )}

            {step !== 'success' && (
                <div style={card}>
                    <h3 style={cardHeader}>Top Up Credits</h3>
                    <p style={{ fontSize:'0.85rem', color:colors.textMuted, marginBottom:'14px' }}>Charged in {config.label} ({currency})</p>
                    {error && <div style={{ backgroundColor:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'8px', padding:'10px', color:'#991B1B', marginBottom:'12px', fontSize:'0.85rem' }}>{error}</div>}

                    {step === 'select' && (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
                            {TOP_UP_AMOUNTS.map(amt => (
                                <button key={amt} type="button" onClick={() => { setSelected(amt); setStep('pay'); setError(''); }}
                                    style={{ padding:'16px 8px', borderRadius:'10px', border:`1.5px solid ${colors.border}`, background:'white', cursor:'pointer', textAlign:'center' }}>
                                    <span style={{ display:'block', fontSize:'1.2rem', fontWeight:700, color:colors.brand }}>{fmt(amt, currency)}</span>
                                    <span style={{ display:'block', fontSize:'0.72rem', color:colors.textFaint, marginTop:'4px' }}>~{amt === 10 ? '5–10' : amt === 20 ? '10–20' : amt === 50 ? '25–50' : '50+'} jobs</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 'pay' && selected && (
                        <div>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                                <span style={{ fontWeight:600, color:colors.text }}>Adding {fmt(selected, currency)}</span>
                                <button onClick={() => { setStep('select'); setSelected(null); setError(''); }} type="button" style={{ background:'none', border:'none', color:colors.textFaint, cursor:'pointer', fontSize:'0.85rem' }}>Change amount</button>
                            </div>
                            <div style={{ border:`1px solid ${colors.border}`, borderRadius:radius.sm, padding:'14px', marginBottom:'16px', backgroundColor:'white' }}>
                                <div ref={mountRef} />
                            </div>
                            <button onClick={handlePay} disabled={paying} style={{ width:'100%', padding:'12px', backgroundColor:colors.brand, color:'white', border:'none', borderRadius:radius.sm, fontWeight:600, fontSize:'0.95rem', cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.7 : 1 }}>
                                {paying ? 'Processing...' : `Pay ${fmt(selected, currency)}`}
                            </button>
                            <p style={{ fontSize:'0.75rem', color:colors.textFaint, textAlign:'center', marginTop:'10px' }}>
                                Secured by Stripe · Test card: 4242 4242 4242 4242
                            </p>
                        </div>
                    )}
                </div>
            )}

            {transactions.length > 0 && (
                <div style={card}>
                    <h3 style={cardHeader}>Recent Transactions</h3>
                    {transactions.map(t => (
                        <div key={t.txn_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'10px 0', borderBottom:`1px solid ${colors.border}`, gap:'10px' }}>
                            <div>
                                <span style={{ display:'inline-block', fontSize:'0.75rem', fontWeight:700, padding:'2px 8px', borderRadius:'999px', marginRight:'8px', backgroundColor: t.amount > 0 ? '#DCFCE7' : '#FEE2E2', color: t.amount > 0 ? '#14532D' : '#991B1B' }}>
                                    {t.txn_type === 'topup' ? '+ Top up' : t.txn_type === 'job_fee' ? '- Job fee' : t.txn_type === 'bonus' ? 'Bonus' : '+ Refund'}
                                </span>
                                <span style={{ fontSize:'0.85rem', color:colors.textMuted }}>{t.description}</span>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'2px', flexShrink:0 }}>
                                <span style={{ fontWeight:700, color: t.amount > 0 ? '#14532D' : '#991B1B' }}>{t.amount > 0 ? '+' : '-'}{fmt(t.amount, t.currency || currency)}</span>
                                <span style={{ fontSize:'0.72rem', color:colors.textFaint }}>{new Date(t.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
